import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { DialogField } from '@/components/ui/dialog-form'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useVerifyTimeEntryCostCurrencyMutation } from '@/hooks/mutations/useTimeEntryMutations'
import { CircleNotchIcon } from '@phosphor-icons/react'

interface CostCurrencyReviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  entryId: string
  expectedVersion: number
  onComplete: () => void
}

export function CostCurrencyReviewDialog({
  open,
  onOpenChange,
  entryId,
  expectedVersion,
  onComplete,
}: CostCurrencyReviewDialogProps) {
  const [currency, setCurrency] = useState('')
  const [sourceReference, setSourceReference] = useState('')
  const [reason, setReason] = useState('')
  const mutation = useVerifyTimeEntryCostCurrencyMutation()

  const resetAndClose = () => {
    setCurrency('')
    setSourceReference('')
    setReason('')
    onOpenChange(false)
  }
  const handleClose = () => {
    if (mutation.isPending) return
    resetAndClose()
  }

  const handleSubmit = async () => {
    if (
      !/^[A-Z]{3}$/.test(currency) ||
      !sourceReference.trim() ||
      !reason.trim()
    )
      return
    try {
      await mutation.mutateAsync({
        id: entryId,
        expectedVersion,
        currency,
        sourceReference: sourceReference.trim(),
        reason: reason.trim(),
      })
      resetAndClose()
      onComplete()
    } catch {
      // The mutation shows the server's refusal and keeps the source text for review.
    }
  }

  return (
    <DialogPattern
      open={open}
      onOpenChange={handleClose}
      title="Review historical cost currency"
      description="Record the currency only after checking the original labor-cost source. The stored rate and amount will not change."
      contentClassName="space-y-4"
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={
              mutation.isPending ||
              !/^[A-Z]{3}$/.test(currency) ||
              !sourceReference.trim() ||
              !reason.trim()
            }
          >
            {mutation.isPending && (
              <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
            )}
            Record currency
          </Button>
        </>
      }
    >
      <DialogField label="Cost currency" htmlFor="review-cost-currency">
        <Input
          id="review-cost-currency"
          autoComplete="off"
          maxLength={3}
          placeholder="USD"
          value={currency}
          onChange={event => setCurrency(event.target.value.toUpperCase())}
        />
      </DialogField>
      <DialogField label="Source reference" htmlFor="review-cost-source">
        <Input
          id="review-cost-source"
          autoComplete="off"
          maxLength={100}
          placeholder="Original payroll, contract, or rate record"
          value={sourceReference}
          onChange={event => setSourceReference(event.target.value)}
        />
      </DialogField>
      <DialogField label="Reason" htmlFor="review-cost-reason">
        <Textarea
          id="review-cost-reason"
          maxLength={1000}
          placeholder="Explain how the source establishes this currency for the work date"
          value={reason}
          onChange={event => setReason(event.target.value)}
        />
      </DialogField>
    </DialogPattern>
  )
}
